/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Alert } from 'antd'
import React from 'react'
import { Container } from 'src/components/Layout'
import GameRosterContent, {
  type GameRosterContentProps,
} from './GameRosterContent'

export interface GameRosterViewProps
  extends Omit<GameRosterContentProps, 'rosterId'> {
  rosterId?: string
  // Playoff placeholder team — no roster until the schedule is set.
  isPlaceholder?: boolean
  // Nobody rostered for this game.
  isEmptyRoster?: boolean
}

const PLACEHOLDER_NOTE =
  'Placeholder Teams do not have Rosters. They will be populated once the playoff schedule is set. Please check back later for updates.'
const EMPTY_ROSTER_NOTE =
  'Looks like this team has no players or officials rostered for this game. Please contact your league administrator for more information.'

/**
 * A team's roster for a game (game page, Roster tab): Players, Spares and Team
 * Officials, or a note when there is no roster to show.
 */
const GameRosterView: React.FC<GameRosterViewProps> = ({
  rosterId,
  isPlaceholder = false,
  isEmptyRoster = false,
  ...contentProps
}) => {
  if (isPlaceholder || isEmptyRoster || !rosterId) {
    return (
      <Container
        noWrap
        column
        css={noticeStyle}
        // Container is plain JS, so TS infers its optional props as required.
        className={undefined}
        forceDesktop={false}
        wrapCss={undefined}
      >
        <Alert
          message='Informational Notes'
          description={isPlaceholder ? PLACEHOLDER_NOTE : EMPTY_ROSTER_NOTE}
          type={isPlaceholder ? 'info' : 'warning'}
          showIcon
        />
      </Container>
    )
  }

  return (
    <Container
      noWrap
      column
      css={rosterStyle}
      className={undefined}
      forceDesktop={false}
      wrapCss={undefined}
    >
      <GameRosterContent rosterId={rosterId} {...contentProps} />
    </Container>
  )
}

export default GameRosterView

// ---- styled ----
const noticeStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 0 8px;
`
const rosterStyle = css`
  min-height: 100vh;
  width: 100%;
  margin: 0 8px 24px 8px;
`
